import { Row } from '@tanstack/react-table';
import { useTable } from '../TableContext';
import { cn } from '../../../../utils/cn';
import React from 'react';
import { DefaultSubComponentToggle } from './DefaultSubComponentToggle';

interface TableSubComponentProps<T> {
  row: Row<T>;
  children: React.ReactNode;
  toggle?: {
    position?: 'before' | 'after';
    render?: (props: {
      row: Row<T>;
      isExpanded: boolean;
      onToggle: () => void;
    }) => React.ReactNode;
  };
}

// Extract common className logic into separate functions
const getToggleRowClassName = (variant: string) =>
  cn('transition-colors', variant === 'rounded' && 'my-2');

const getToggleCellClassName = (
  variant: string,
  isExpanded: boolean,
  togglePosition: string,
) =>
  cn(
    'p-0',
    'overflow-hidden',
    'border-table-border border',
    variant === 'rounded'
      ? [
          isExpanded && [
            'bg-gray-50 dark:bg-gray-800',
            togglePosition === 'before'
              ? 'rounded-b-none border-b-0'
              : 'border-t',
            togglePosition === 'after' && 'rounded-b-2xl',
          ],
          !isExpanded && [
            'hover:bg-gray-50 dark:hover:bg-gray-800',
            'rounded-b-2xl',
          ],
        ]
      : [
          isExpanded && [
            'bg-gray-50 dark:bg-gray-800',
            togglePosition === 'before' ? 'border-b-0' : 'border-t',
          ],
        ],
  );

const getContentCellClassName = (togglePosition: string) =>
  cn(
    'p-0',
    'border-table-border border',
    togglePosition === 'after' && 'border-b-0',
    togglePosition === 'before' && 'border-t-0',
  );

export function TableSubComponent<T>({
  row,
  children,
  toggle = { position: 'before', render: DefaultSubComponentToggle },
}: TableSubComponentProps<T>) {
  const { variant = 'rounded' } = useTable<T>();
  const togglePosition = toggle?.position || 'after';
  const toggleContent = toggle?.render || DefaultSubComponentToggle;

  const toggleRow = (
    <tr key={`toggle-${row.id}`} className={getToggleRowClassName(variant)}>
      <td
        colSpan={row.getVisibleCells().length}
        className={getToggleCellClassName(
          variant,
          row.getIsExpanded(),
          togglePosition,
        )}
      >
        {toggleContent({
          row,
          isExpanded: row.getIsExpanded(),
          onToggle: () => row.toggleExpanded(),
        })}
      </td>
    </tr>
  );

  const contentRow = row.getIsExpanded() && (
    <tr key={`content-${row.id}`} className={getToggleRowClassName(variant)}>
      <td
        colSpan={row.getVisibleCells().length}
        className={getContentCellClassName(togglePosition)}
      >
        <div>{children}</div>
      </td>
    </tr>
  );

  return (
    <>
      {togglePosition === 'before' && toggleRow}
      {contentRow}
      {togglePosition === 'after' && toggleRow}
    </>
  );
}

TableSubComponent.displayName = 'TableSubComponent';
